import { Badge } from "@/components/ui/badge";

export default function SDLCReference() {
  return (
    <div className="p-6 lg:p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-foreground">
          SDLC Reference
        </h1>
        <p className="text-sm text-muted-foreground font-body mt-1">
          Software Development Life Cycle — Agile vs Waterfall methodologies
        </p>
      </div>

      {/* Agile vs Waterfall comparison */}
      <div className="grid sm:grid-cols-2 gap-6 mb-10">
        <div className="border border-border rounded-md p-5 bg-card">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-accent text-accent-foreground">Agile</Badge>
            <span className="text-xs text-muted-foreground font-mono">Since ~2012</span>
          </div>
          <ul className="space-y-2 text-sm font-body text-card-foreground">
            <li className="flex gap-2"><span className="text-accent">→</span> Sprint-based: 2–4 week cycles</li>
            <li className="flex gap-2"><span className="text-accent">→</span> Iterative delivery, continuous feedback</li>
            <li className="flex gap-2"><span className="text-accent">→</span> User stories, epics, story points</li>
            <li className="flex gap-2"><span className="text-accent">→</span> Daily standups, sprint planning, retrospectives</li>
            <li className="flex gap-2"><span className="text-accent">→</span> Changes welcomed mid-project</li>
            <li className="flex gap-2"><span className="text-accent">→</span> Scrum or Kanban frameworks</li>
            <li className="flex gap-2"><span className="text-accent">→</span> Jira boards for tracking</li>
          </ul>
        </div>
        <div className="border border-border rounded-md p-5 bg-card">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-secondary text-secondary-foreground">Waterfall</Badge>
            <span className="text-xs text-muted-foreground font-mono">Traditional</span>
          </div>
          <ul className="space-y-2 text-sm font-body text-card-foreground">
            <li className="flex gap-2"><span className="text-muted-foreground">→</span> Sequential: top to bottom, one-way</li>
            <li className="flex gap-2"><span className="text-muted-foreground">→</span> Gathering → Dev → QA → SIT → UAT → Go Live</li>
            <li className="flex gap-2"><span className="text-muted-foreground">→</span> BRD, FRD, SRS documents</li>
            <li className="flex gap-2"><span className="text-muted-foreground">→</span> Each phase ~1 month, UAT ~2 weeks</li>
            <li className="flex gap-2"><span className="text-muted-foreground">→</span> Cannot reverse — costly if requirements wrong</li>
            <li className="flex gap-2"><span className="text-muted-foreground">→</span> Formal sign-offs between phases</li>
            <li className="flex gap-2"><span className="text-muted-foreground">→</span> Slowly being replaced by Agile</li>
          </ul>
        </div>
      </div>

      {/* Scrum vs Kanban */}
      <div className="mb-10">
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">
          Scrum vs Kanban
        </h2>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm font-body">
            <thead className="bg-secondary/50">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-mono text-muted-foreground uppercase">Aspect</th>
                <th className="text-left px-4 py-2.5 text-xs font-mono text-muted-foreground uppercase">Scrum</th>
                <th className="text-left px-4 py-2.5 text-xs font-mono text-muted-foreground uppercase">Kanban</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr><td className="px-4 py-2.5 font-semibold">Cadence</td><td className="px-4 py-2.5">Fixed sprints (2–4 weeks)</td><td className="px-4 py-2.5">Continuous flow</td></tr>
              <tr><td className="px-4 py-2.5 font-semibold">Roles</td><td className="px-4 py-2.5">Scrum Master, Product Owner, Dev Team</td><td className="px-4 py-2.5">No predefined roles</td></tr>
              <tr><td className="px-4 py-2.5 font-semibold">Changes</td><td className="px-4 py-2.5">Not encouraged mid-sprint</td><td className="px-4 py-2.5">Can happen anytime</td></tr>
              <tr><td className="px-4 py-2.5 font-semibold">Board</td><td className="px-4 py-2.5">Reset each sprint</td><td className="px-4 py-2.5">Persistent, WIP limits</td></tr>
              <tr><td className="px-4 py-2.5 font-semibold">Metrics</td><td className="px-4 py-2.5">Sprint velocity</td><td className="px-4 py-2.5">Lead time, cycle time</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Environments */}
      <div className="mb-10">
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">
          Environments
        </h2>
        <div className="flex flex-wrap gap-3">
          {[
            { name: "DEV", desc: "Development — engineers build features", color: "bg-secondary text-secondary-foreground" },
            { name: "QA", desc: "Testing — QA team validates (SIT)", color: "bg-secondary text-secondary-foreground" },
            { name: "UAT", desc: "Business owners test & sign off", color: "bg-accent/20 text-accent" },
            { name: "PROD", desc: "Production — public access", color: "bg-accent text-accent-foreground" },
          ].map((env) => (
            <div key={env.name} className="border border-border rounded-md p-4 bg-card flex-1 min-w-[200px]">
              <Badge className={env.color}>{env.name}</Badge>
              <p className="text-xs text-muted-foreground mt-2 font-body">{env.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Documentation Types */}
      <div className="mb-10">
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">
          Documentation Types
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <DocCard
            title="BRD — Business Requirements Document"
            desc="High-level business needs and objectives. Used in waterfall or when external vendors (like IBM) need requirements without Jira access."
          />
          <DocCard
            title="FRD — Functional Requirements Document"
            desc="Detailed functional specifications derived from BRD. Describes what the system must do."
          />
          <DocCard
            title="SRS — Software Requirements Specification"
            desc="Technical requirements including system behavior, interfaces, and constraints."
          />
          <DocCard
            title="User Stories (Agile)"
            desc="As a [role], I want [feature], so that [benefit]. Includes acceptance criteria and story points."
          />
        </div>
      </div>

      {/* Testing Types */}
      <div className="mb-10">
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">
          Testing Stages
        </h2>
        <div className="border border-border rounded-md p-5 bg-card">
          <ol className="space-y-3 text-sm font-body">
            <li className="flex gap-3">
              <span className="text-accent font-mono font-bold w-5">1</span>
              <div><span className="font-semibold">Unit Testing</span> — Individual components tested by developers</div>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-mono font-bold w-5">2</span>
              <div><span className="font-semibold">System Integration Testing (SIT)</span> — QA team tests integrated system</div>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-mono font-bold w-5">3</span>
              <div><span className="font-semibold">API Testing</span> — SOAP/REST endpoint validation</div>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-mono font-bold w-5">4</span>
              <div><span className="font-semibold">UAT</span> — Business owners validate before go-live sign-off</div>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-mono font-bold w-5">5</span>
              <div><span className="font-semibold">Regression Testing</span> — Ensure existing functionality still works</div>
            </li>
          </ol>
        </div>
      </div>

      {/* Key Agile Ceremonies */}
      <div className="mb-10">
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">
          Agile Ceremonies
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <DocCard title="Sprint Planning" desc="Team selects user stories for the sprint based on velocity and priority." />
          <DocCard title="Daily Standup" desc="15-min sync: what did I do, what will I do, any blockers?" />
          <DocCard title="Sprint Review" desc="Demo completed work to stakeholders. Product Owner, Scrum Master, and team present." />
          <DocCard title="Sprint Retrospective" desc="What went well, what didn't, what to improve. 3 pillars: transparency, inspection, adaptation." />
        </div>
      </div>

      {/* Scope Creep */}
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">
          Change Control & Scope Creep
        </h2>
        <div className="border border-border rounded-md p-5 bg-card text-sm font-body space-y-2">
          <p><span className="font-semibold">Scope creep:</span> Uncontrolled additions without checking impact on scope, time, and cost.</p>
          <p><span className="font-semibold">Prevention:</span></p>
          <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
            <li>Communicate vision clearly — ensure team alignment</li>
            <li>Review requirements regularly against what's delivered</li>
            <li>All changes go through change control process via Jira</li>
            <li>Get approval on change requests before implementation</li>
            <li>Document everything — record calls, follow up in writing</li>
            <li>Be very polite with business owners — they fund the project</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function DocCard({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="border border-border rounded-md p-4 bg-card">
      <h3 className="font-display font-semibold text-sm text-card-foreground mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground font-body">{desc}</p>
    </div>
  );
}
